import {registerGlobalInitFunc} from '../modules/observer.ts';

// Lowest to highest. Must match api.SmartReviewSeverityThreshold on the server.
export const SEVERITY_LEVELS = ['recommendation', 'warning', 'error', 'summary_only'] as const;
export type SeverityLevel = typeof SEVERITY_LEVELS[number];

export function normalizeSeverityLevel(value: string | null | undefined): SeverityLevel {
  return (SEVERITY_LEVELS as readonly string[]).includes(value ?? '') ? value as SeverityLevel : 'recommendation';
}

// Binds one settings-page slider (templates/shared/smart_review/
// severity_slider.tmpl). The range input is the control and the hidden input is
// what the form submits, so the server only ever sees level names.
//
// The slider is editable only while the "custom" mode radio is checked. In the
// other modes it is a read-only preview of the level that actually applies: the
// organization's level for "inherit" (data-inherit-value), DRCY's default for
// the rest. Switching to "custom" starts from whatever applied before, so saving
// without touching the slider keeps the current behavior.
export function bindSeveritySlider(root: HTMLElement) {
  const range = root.querySelector<HTMLInputElement>('input[type="range"]');
  const hidden = root.querySelector<HTMLInputElement>('input[type="hidden"]');
  const labels = root.querySelectorAll<HTMLElement>('.smart-review-severity-labels [data-level]');
  const stops = root.querySelectorAll<HTMLElement>('.smart-review-severity-stop');
  if (!range || !hidden) return;

  const modeName = root.getAttribute('data-mode-name');
  const form = root.closest('form');
  const inheritLevel = normalizeSeverityLevel(root.getAttribute('data-inherit-value'));
  const saved = root.getAttribute('data-value');
  // The custom-mode choice; null until one exists (nothing saved, never custom).
  let chosen: SeverityLevel | null = saved ? normalizeSeverityLevel(saved) : null;

  const modeRadios = form ? Array.from(form.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${modeName}"]`)) : [];
  const checkedMode = () => modeRadios.find((radio) => radio.checked)?.value ?? 'custom';
  const appliedLevel = (mode: string): SeverityLevel => (mode === 'inherit' ? inheritLevel : 'recommendation');
  let mode = checkedMode();

  const render = () => {
    const editable = mode === 'custom';
    const level = editable ? chosen ?? appliedLevel(mode) : appliedLevel(mode);
    const index = SEVERITY_LEVELS.indexOf(level);
    range.disabled = !editable;
    root.classList.toggle('disabled', !editable);
    hidden.value = level;
    range.value = String(index);
    const label = Array.from(labels).find((element) => element.getAttribute('data-level') === level);
    range.setAttribute('aria-valuetext', label?.textContent.trim() || level);
    // A read-only preview shows where the thumb sits but reads as unselected.
    const reached = editable ? index : -1;
    root.style.setProperty('--severity-fill', String(Math.max(reached, 0) / (SEVERITY_LEVELS.length - 1)));
    for (const element of labels) {
      element.classList.toggle('active', element.getAttribute('data-level') === level);
    }
    for (const [position, stop] of stops.entries()) {
      stop.classList.toggle('filled', position <= reached);
    }
  };
  const choose = (level: SeverityLevel) => {
    if (range.disabled) return;
    chosen = level;
    render();
  };

  range.addEventListener('input', () => choose(SEVERITY_LEVELS[Number(range.value)] ?? 'recommendation'));
  for (const label of labels) {
    label.addEventListener('click', () => choose(normalizeSeverityLevel(label.getAttribute('data-level'))));
  }
  if (form && modeRadios.length) {
    form.addEventListener('change', (event) => {
      if ((event.target as HTMLInputElement | null)?.name !== modeName) return;
      const next = checkedMode();
      if (next === 'custom' && chosen === null) chosen = appliedLevel(mode);
      mode = next;
      render();
    });
  }
  render();
}

export function initSmartReviewSeveritySlider() {
  registerGlobalInitFunc('initSmartReviewSeveritySlider', bindSeveritySlider);
}
